/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import { View } from 'backbone.marionette';
import { Radio } from 'backbone';

import GridView from '@/views/GridView';
import PageableCollection from '@/backbone/PageableCollection';
import ButtonCell from '@/backgrid/ButtonCell';
import StringCell from '@/backgrid/StringCell';
import { DateCell } from '@/backgrid/MomentCell';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import openInNewTab from '@/util/openInNewTab';
import OrderTypes from '@/enums/OrderTypes';

import SaveOrderPackageOrdersSelection from '../../services/SaveOrderPackageOrdersSelection';

import './OrderPackageSelectOrdersView.css';

import tmplOrderPackageSelectOrdersView from './OrderPackageSelectOrdersView.hbs';

const OrderPackageSelectOrdersView = View.extend({
  template: tmplOrderPackageSelectOrdersView,

  className: 'order-package-select-orders-view',

  ui: {
    approvedOrdersGrid: '.approved-orders-grid',
    packagedOrdersGrid: '.packaged-orders-grid'
  },

  regions: {
    approvedOrdersGrid: '@ui.approvedOrdersGrid',
    packagedOrdersGrid: '@ui.packagedOrdersGrid'
  },

  templateContext() {
    return { editable: this.options.editable };
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  onRender() {
    const approvedOrders = this.model.get('approvedOrders');
    const packagedOrders = this.model.get('packagedOrders');
    const editable = this.options.editable;

    const commonColumns = [
      {
        name: 'id',
        label: 'Order ID',
        cell: StringCell,
        headerCell: 'custom',
        width: 6,
        editable: false
      },
      {
        name: 'formName',
        label: 'Order Type',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false
      },
      {
        name: 'status',
        label: 'Order Status',
        cell: StringCell,
        headerCell: 'custom',
        width: 9,
        editable: false
      },
      {
        name: 'trackerNumber',
        label: 'Tracker Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 9,
        editable: false
      },
      {
        name: 'replacementPIINSPIIN',
        label: 'Contract Number (PIIN/SPIIN)',
        cell: StringCell,
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'deviceCount',
        label: 'Number of Devices',
        cell: StringCell,
        headerCell: 'custom',
        width: 9,
        editable: false
      },
      {
        name: 'submittedOn',
        label: 'Submitted On',
        cell: DateCell,
        headerCell: 'custom',
        width: 9,
        editable: false
      },
      {
        name: 'approvedOn',
        label: 'Approved By CRS On',
        cell: DateCell,
        headerCell: 'custom',
        width: 9,
        editable: false
      },
      {
        name: 'popStartDate',
        label: 'POP Start Date',
        cell: DateCell,
        headerCell: 'custom',
        width: 9,
        editable: false
      },
      {
        name: 'open',
        label: '',
        width: 3,
        cell: ButtonCell.extend({
          name: 'Open',
          buttonClass: 'btn-sm btn-outline-primary',
          onClick: this.viewDetails.bind(this),
          newTab: true
        }),
        editable: false,
        sortable: false
      }
    ];

    if (editable) {
      const approvedCols = [
        {
          name: 'add',
          label: '',
          width: 5,
          cell: ButtonCell.extend({
            name: '',
            buttonClass: 'btn-sm btn-success text-white add-remove-symbol',
            disabled: !editable,
            labelClass: 'fa fa-plus fa-lg fa-fw text-success',
            onClick: this.addToPackage.bind(this)
          }),
          editable: false,
          sortable: false,
          renderable: !!editable,
          ariaLabel: 'Add Order to Order Package'
        }
      ].concat(commonColumns);
      approvedCols.filter((col) => col.renderable !== false);

      this.approvedOrdersGrid = new GridView({
        columns: approvedCols,
        collection: new PageableCollection(approvedOrders, { pageSize: 7 }),
        emptyText: 'No Approved Orders Found'
      });

      this.showChildView('approvedOrdersGrid', this.approvedOrdersGrid);
    }

    const packagedCols = [
      {
        name: 'remove',
        label: '',
        width: 5,
        cell: ButtonCell.extend({
          name: '',
          buttonClass: 'btn-sm btn-danger text-white add-remove-symbol',
          disabled: !editable,
          labelClass: 'fa fa-minus fa-lg fa-fw text-danger',
          onClick: this.removeFromPackage.bind(this)
        }),
        editable: false,
        sortable: false,
        renderable: !!editable,
        ariaLabel: 'Remove Order from Order Package'
      }
    ].concat(commonColumns);
    packagedCols.filter((col) => col.renderable !== false);

    this.packagedOrdersGrid = new GridView({
      columns: packagedCols,
      collection: new PageableCollection(packagedOrders, { pageSize: 7 }),
      emptyText: 'No Packaged Orders'
    });

    this.showChildView('packagedOrdersGrid', this.packagedOrdersGrid);
  },

  addToPackage(e, model) {
    const length = this.packagedOrdersGrid.collection.fullCollection.length;
    const modelOrderFormTypeID = model.get('orderFormTypeID');
    const hasSamePIINSPIIN = this.packagedOrdersGrid.collection.fullCollection.every({
      replacementPIINSPIIN: model.get('replacementPIINSPIIN')
    });
    let firstOrderTypeAdded;
    if(length) firstOrderTypeAdded = this.packagedOrdersGrid.collection.fullCollection.first().get('orderFormTypeID')
    if (
      length &&
      firstOrderTypeAdded === OrderTypes.RELOCATION &&
      firstOrderTypeAdded === modelOrderFormTypeID &&
      !hasSamePIINSPIIN
    ) {
      showErrorMessage({
        errorCode: 'relocationContractError',
        errorTitle: 'Relocation Contract Number Error',
        errorMessage: 'Only relocation orders with the same contract number can be added.'
      });
      return;
    }
    if (
      length
        ? firstOrderTypeAdded === modelOrderFormTypeID
        : true
    ) {
      this.approvedOrdersGrid.collection.fullCollection.remove(model);
      this.packagedOrdersGrid.collection.fullCollection.unshift(model);
    } else {
      showErrorMessage({
        errorCode: 'orderTypeError',
        errorTitle: 'Order Type Error',
        errorMessage: 'Only orders of the same type can be added.'
      });
    }
  },

  removeFromPackage(e, model) {
    this.packagedOrdersGrid.collection.fullCollection.remove(model);
    this.approvedOrdersGrid.collection.fullCollection.unshift(model);
  },

  viewDetails(e, model) {
    e.preventDefault();
    openInNewTab(`#order/${model.id}`);
  },

  async onSave(e) {
    this.toggleForm(false);
    const orderFormTypeID = this.packagedOrdersGrid.collection.fullCollection.length
      ? this.packagedOrdersGrid.collection.fullCollection.first().toJSON().orderFormTypeID
      : null;
    const orderFormName = this.packagedOrdersGrid.collection.fullCollection.length
      ? this.packagedOrdersGrid.collection.fullCollection.first().toJSON().formName
      : null;
    const orders = _.map(this.packagedOrdersGrid.collection.fullCollection.toJSON(), (x) => {
      return { ID: x.id };
    });

    try {
      await SaveOrderPackageOrdersSelection(this.options.orderPackageID, orderFormTypeID, orders);

      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'Order Package Orders Saved.',
        hideAfter: 1
      });

      // Flip statuses in the grids
      _.each(this.packagedOrdersGrid.collection.fullCollection.models, (model) => {
        model.set('status', 'Packaged');
        model.set('statusID', '6');
      });

      _.each(this.approvedOrdersGrid.collection.fullCollection.models, (model) => {
        model.set('status', 'Approved');
        model.set('statusID', '5');
      });

      // Update orderType in OrderPackageHeaderView
      Radio.channel('order-package').trigger('update:header', 'formName', orderFormName);
    } catch (err) {
      throw new Error(err);
    } finally {
      this.toggleForm(true);
    }
  }
});

export default OrderPackageSelectOrdersView;
